import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { memberAuthAPI } from '../../utils/api';
import { toast } from 'react-toastify';
import LogoImage from '../../components/ui/LogoImage';

export default function MemberRegisterPage() {
  const navigate = useNavigate();
  const [step, setStep]     = useState(1);
  const [saving, setSaving] = useState(false);
  const [form, setForm]     = useState({
    full_name: '', email: '', password: '', confirm_password: '', phone: '',
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Step 1 → Step 2
  const goToStep2 = (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.password)
      return toast.error('Please fill all required fields.');
    if (form.password.length < 8)
      return toast.error('Password must be at least 8 characters.');
    if (form.password !== form.confirm_password)
      return toast.error('Passwords do not match.');
    setStep(2);
  };

  // Step 2 → Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await memberAuthAPI.register({
        full_name: form.full_name,
        email:     form.email,
        password:  form.password,
        phone:     form.phone,
      });
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Step 3: Success screen ────────────────────────────────
  if (step === 3) return (
    <div className="min-h-screen pattern-bg flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-8 text-center">
        <div className="text-5xl mb-4">📬</div>
        <h2 className="text-2xl font-bold text-navy-800 mb-3">Check Your Email!</h2>
        <p className="text-gray-500 mb-1">We've sent a verification link to:</p>
        <p className="font-bold text-cyan-600 mb-5">{form.email}</p>
        <div className="bg-navy-50 border border-navy-100 rounded-2xl p-4 text-sm text-navy-700 text-left space-y-2 mb-6">
          <p>✅ Click the link in your email to verify your account</p>
          <p>⏳ An admin will then assign your membership role</p>
          <p>📧 You'll receive an email once your role is assigned</p>
        </div>
        <Link to="/" className="btn-primary inline-block mb-3">← Back to Website</Link>
        <p className="text-sm text-gray-400">
          Already verified?{' '}
          <Link to="/member/login" className="text-cyan-600 font-semibold hover:underline">
            Sign in here
          </Link>
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen pattern-bg flex items-center justify-center p-4">
      <div className="w-full max-w-lg">

        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex justify-center mb-3">
            <LogoImage
              size={72}
              className="rounded-full border-4 border-cyan-400 shadow-lg"
            />
          </div>
          <h1 className="text-2xl font-bold text-navy-800">Join JNF</h1>
          <p className="text-gray-500 text-sm mt-1">Register as a member of the JAMA</p>
        </div>

        <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8">

          {/* Step indicator */}
          <div className="flex items-center gap-2 mb-8">
            {['Account Details', 'Membership Info'].map((label, i) => {
              const s = i + 1;
              const done   = step > s;
              const active = step === s;
              return (
                <div key={i} className="flex items-center flex-1">
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                      done   ? 'bg-green-500 text-white' :
                      active ? 'bg-navy-700 text-white' :
                               'bg-gray-100 text-gray-400'
                    }`}>
                      {done ? '✓' : s}
                    </div>
                    <span className={`text-xs font-medium ${active ? 'text-navy-700' : 'text-gray-400'}`}>
                      {label}
                    </span>
                  </div>
                  {i < 1 && (
                    <div className={`flex-1 h-0.5 mx-3 transition-all ${done ? 'bg-green-400' : 'bg-gray-200'}`} />
                  )}
                </div>
              );
            })}
          </div>

          {/* ── Step 1: Account details ── */}
          {step === 1 && (
            <form onSubmit={goToStep2} className="space-y-4">
              <div>
                <label className="form-label">Full Name *</label>
                <input
                  required className="form-input"
                  value={form.full_name}
                  onChange={e => set('full_name', e.target.value)}
                  placeholder="e.g. Abdullahi Muhammad"
                />
              </div>
              <div>
                <label className="form-label">Email Address *</label>
                <input
                  required type="email" className="form-input"
                  value={form.email}
                  onChange={e => set('email', e.target.value)}
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <label className="form-label">Phone Number</label>
                <input
                  type="tel" className="form-input"
                  value={form.phone}
                  onChange={e => set('phone', e.target.value)}
                  placeholder="+234 800 000 0000"
                />
              </div>
              <div>
                <label className="form-label">Password *</label>
                <input
                  required type="password" className="form-input"
                  value={form.password}
                  onChange={e => set('password', e.target.value)}
                  placeholder="Minimum 8 characters"
                />
              </div>
              <div>
                <label className="form-label">Confirm Password *</label>
                <input
                  required type="password" className="form-input"
                  value={form.confirm_password}
                  onChange={e => set('confirm_password', e.target.value)}
                  placeholder="Repeat your password"
                />
              </div>
              <button type="submit" className="btn-primary w-full mt-2">
                Continue →
              </button>
            </form>
          )}

          {/* ── Step 2: Membership info ── */}
          {step === 2 && (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-800">
                <p className="font-semibold mb-1">📋 About Membership Roles</p>
                <p className="text-xs leading-relaxed">
                  After verifying your email, an admin will review your registration
                  and assign your membership role based on your Tijaniyyah journey.
                  You will be notified by email once your role is assigned.
                </p>
              </div>

              <div className="bg-navy-50 border border-navy-100 rounded-2xl p-4">
                <p className="text-sm font-semibold text-navy-700 mb-3">Roles available in JNF:</p>
                <div className="space-y-2">
                  {[
                    { icon:'💛', label:'The Lovers',     ar:'المحبون',   desc:'Seekers interested in the Tijaniyyah path' },
                    { icon:'📖', label:'General Member', ar:'عضو عام',   desc:'Members growing in knowledge of Allah' },
                    { icon:'✅', label:'Fully Member',   ar:'عضو كامل',  desc:'Initiated members with knowledge of Allah' },
                    { icon:'🌟', label:'Muqaddam',       ar:'مقدّم',     desc:'Senior members authorised to guide others' },
                  ].map(r => (
                    <div key={r.label} className="flex items-center gap-3 text-sm">
                      <span className="text-lg flex-shrink-0">{r.icon}</span>
                      <div>
                        <span className="font-semibold text-navy-700">{r.label}</span>
                        <span className="arabic text-navy-500 text-xs mx-2">{r.ar}</span>
                        <span className="text-gray-400 text-xs">— {r.desc}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="btn-outline flex-1 py-2.5 text-sm"
                >
                  ← Back
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary flex-1 py-2.5 text-sm flex items-center justify-center gap-2"
                >
                  {saving
                    ? <><div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Submitting...</>
                    : 'Complete Registration'}
                </button>
              </div>
            </form>
          )}

          {/* Footer links */}
          <div className="mt-6 pt-5 border-t border-gray-100 text-center space-y-2">
            <p className="text-sm text-gray-500">
              Already have an account?{' '}
              <Link to="/member/login" className="text-cyan-600 font-semibold hover:underline">
                Sign in
              </Link>
            </p>
            <Link to="/" className="block text-gray-400 text-xs hover:text-navy-600 transition-colors">
              ← Back to Website
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
